// ANYCAST globe: WebGL2 dot-matrix planet with atmosphere, day/night, data
// centers, players, packets (comets on lifted great-circle arcs), ripples and cursors.

import { arcPoints, clamp, latLonToVec3, vec3ToLatLon, type LatLon, type Vec3 } from "../../shared/geo";
import { COLOS, coloByIata } from "../../shared/colos";
import { createProgram, DynamicBuffer, FloatStream, IndexStream, setupAttribs, type Program } from "./gl";
import { loadLandDots, type LandDots } from "./land";
import {
  easeInOutCubic,
  globeRotation,
  hash01,
  hsl,
  invert4,
  lerpAngleDeg,
  mat4,
  mul4,
  shiftedPerspective,
  translation,
  xform4,
  DEG,
  type Mat4,
} from "./math";
import { DOTS_FS, DOTS_VS, GLOBE_FS, GLOBE_VS, RIBBON_FS, RIBBON_VS, SPRITE_FS, SPRITE_VS } from "./shaders";

export interface GlobePlayer {
  id: string;
  lat: number;
  lon: number;
  hue: number;
  ghost?: boolean;
  isYou?: boolean;
  colo?: string;
}

export interface GlobeOptions {
  attachPointerHandlers?: boolean;
  /** default "/assets/land-110m.json" */
  landUrl?: string;
  /** Number of Fibonacci samples over the whole sphere (land keeps ~29%). Default 56000. */
  dotSamples?: number;
  /** Called once a pointer gesture turns into a drag (moved > 6px). */
  onDragStart?: () => void;
  /** Called when a drag gesture ends. */
  onDragEnd?: () => void;
}

export interface PacketOptions {
  width?: number;
  onArrive?: () => void;
  onWaypoint?: (i: number) => void;
}

// ---------------------------------------------------------------- constants

const CAM_DIST = 3.6;
const MAX_TILT = 70;
const AUTO_SPIN_DEG_PER_MS = 360 / 90_000;
const IDLE_RESUME_MS = 3500;
const TAP_SLOP_PX = 6;
const MAX_PACKETS = 320;
const MAX_RIPPLES = 200;
const RIPPLE_MS = 1200;
const FLASH_MS = 520;
const TRAIL_LINGER_MS = 1000;
const ARC_R = 1.003;

const CF_ORANGE: Vec3 = [0.953, 0.502, 0.125];
const HOME_COL: Vec3 = [1.0, 0.8, 0.58];

// sprite shapes / modes
const S_GLOW = 0, S_RING = 1, S_CROSS = 2, S_BEACON = 3, S_SOFT = 4;
const BILLBOARD = 0, TANGENT = 1;

const RIBBON_FLOATS = 14;
const SPRITE_FLOATS = 12;

// ---------------------------------------------------------------- internal types

interface Arc {
  pts: Float32Array; // (n+1)*3
  tans: Float32Array; // (n+1)*3
  n: number; // segments
}

interface Leg extends Arc {
  t0: number;
  dur: number;
}

interface Packet {
  legs: Leg[];
  rgb: Vec3;
  width: number;
  start: number;
  total: number;
  nextWp: number; // next waypoint index to flash (1..legs.length)
  arrived: boolean;
  onArrive?: () => void;
  onWaypoint?: (i: number) => void;
}

interface Ripple {
  p: Vec3;
  rgb: Vec3;
  strength: number;
  born: number;
}

interface Flash {
  p: Vec3;
  rgb: Vec3;
  born: number;
}

interface Cursor {
  cur: Vec3;
  target: Vec3;
  rgb: Vec3;
}

interface PlayerRec {
  p: GlobePlayer;
  pos: Vec3;
  rgb: Vec3;
  phase: number;
  arc: Arc | null;
}

interface GLRes {
  globe: Program;
  dots: Program;
  ribbon: Program;
  sprite: Program;
  globeVao: WebGLVertexArrayObject | null;
  dotsVao: WebGLVertexArrayObject | null;
  dotsBuf: WebGLBuffer | null;
  dotsCount: number;
  ribbonVao: WebGLVertexArrayObject | null;
  ribbonVerts: DynamicBuffer;
  ribbonIdx: DynamicBuffer;
  spriteVao: WebGLVertexArrayObject | null;
  spriteInst: DynamicBuffer;
  quad: WebGLBuffer | null;
}

// ---------------------------------------------------------------- helpers

function makeArc(a: LatLon, b: LatLon, radius = ARC_R): Arc {
  const va = latLonToVec3(a.lat, a.lon);
  const vb = latLonToVec3(b.lat, b.lon);
  const ang = Math.acos(clamp(va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2], -1, 1));
  const n = Math.max(4, Math.min(64, Math.ceil(ang / DEG / 2.5)));
  const ptsArr = arcPoints(a, b, n, radius);
  return fromPoints(ptsArr);
}

function fromPoints(ptsArr: Vec3[]): Arc {
  const n = ptsArr.length - 1;
  const pts = new Float32Array((n + 1) * 3);
  ptsArr.forEach((p, i) => pts.set(p, i * 3));
  const tans = new Float32Array((n + 1) * 3);
  for (let i = 0; i <= n; i++) {
    const a = Math.max(0, i - 1);
    const b = Math.min(n, i + 1);
    let tx = pts[b * 3]! - pts[a * 3]!;
    let ty = pts[b * 3 + 1]! - pts[a * 3 + 1]!;
    let tz = pts[b * 3 + 2]! - pts[a * 3 + 2]!;
    const l = Math.hypot(tx, ty, tz) || 1;
    tx /= l; ty /= l; tz /= l;
    tans[i * 3] = tx; tans[i * 3 + 1] = ty; tans[i * 3 + 2] = tz;
  }
  return { pts, tans, n };
}

function mix3(a: Vec3, b: Vec3, t: number): Vec3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

function safeCall(fn: (() => void) | undefined): void {
  if (!fn) return;
  try {
    fn();
  } catch (err) {
    console.error("globe callback failed", err);
  }
}

// ---------------------------------------------------------------- Globe

export class Globe {
  ready: Promise<void>;
  /** 0..1 audio level set by the host; subtly brightens atmosphere and dots. */
  energy = 0;

  private canvas: HTMLCanvasElement;
  private opts: GlobeOptions;
  private gl: WebGL2RenderingContext | null = null;
  private res: GLRes | null = null;
  private disposed = false;

  // viewport (CSS px)
  private cssW = 1;
  private cssH = 1;
  private dpr = 1;
  private vpCx = 0;
  private vpCy = 0;
  private vpR = 100;
  private viewportSet = false;

  // camera
  private camLat = 22;
  private camLon = 10;
  private velLat = 0; // deg/ms
  private velLon = 0;
  private lastInteraction = -Infinity;
  private spin = 1;
  private focusAnim: { fromLat: number; fromLon: number; toLat: number; toLon: number; start: number; dur: number } | null =
    null;
  private mvp: Mat4 = mat4();
  private invMvp: Mat4 = mat4();
  private cam: Vec3 = [0, 0, CAM_DIST];
  private lastNow = -1;
  private time = 0;

  // pointer
  private ptr: { id: number; x0: number; y0: number; x: number; y: number; t: number; dragging: boolean } | null = null;
  private lastGestureWasDrag = false;

  // scene
  private land: LandDots | null = null;
  private sun: Vec3 = [0, 0, 1];
  private home: Vec3 | null = null;
  private homeLL: LatLon | null = null;
  private players: PlayerRec[] = [];
  private active = new Set<string>();
  private activeArcs: Arc[] = [];
  private cursors = new Map<string, Cursor>();
  private ripples: Ripple[] = [];
  private flashes: Flash[] = [];
  private packets: Packet[] = [];
  private coloPos: Array<{ iata: string; pos: Vec3; phase: number }>;

  // per-frame streams
  private rv = new FloatStream(1 << 16);
  private ri = new IndexStream(1 << 14);
  private sv = new FloatStream(1 << 14);
  private ribbonOpen = false;
  private ribbonCount = 0;

  private listeners: Array<[EventTarget, string, EventListener]> = [];

  constructor(canvas: HTMLCanvasElement, opts: GlobeOptions = {}) {
    this.canvas = canvas;
    this.opts = opts;
    this.coloPos = COLOS.map((c) => ({ iata: c.iata, pos: latLonToVec3(c.lat, c.lon, 1.002), phase: hash01(c.iata) }));

    this.on(canvas, "webglcontextlost", (e) => {
      e.preventDefault();
      this.res = null;
    });
    this.on(canvas, "webglcontextrestored", () => this.initGL());
    this.on(window, "resize", () => this.resize());

    if (opts.attachPointerHandlers !== false) {
      canvas.style.touchAction = "none";
      this.on(canvas, "pointerdown", (e) => this.handlePointerDown(e as PointerEvent));
      this.on(canvas, "pointermove", (e) => this.handlePointerMove(e as PointerEvent));
      this.on(canvas, "pointerup", (e) => this.handlePointerUp(e as PointerEvent));
      this.on(canvas, "pointercancel", (e) => this.handlePointerUp(e as PointerEvent));
    }

    this.gl = canvas.getContext("webgl2", {
      alpha: true,
      premultipliedAlpha: true,
      antialias: true,
      depth: false,
      stencil: false,
      powerPreference: "high-performance",
      preserveDrawingBuffer: false,
    });
    if (!this.gl) console.warn("globe: WebGL2 unavailable");
    this.resize();
    this.initGL();

    const samples = opts.dotSamples ?? 56000;
    this.ready = loadLandDots(opts.landUrl ?? "/assets/land-110m.json", samples, COLOS)
      .then((land) => {
        this.land = land;
        this.uploadLand();
      })
      .catch((err: unknown) => {
        console.warn("globe: land unavailable", err);
      });
  }

  // ------------------------------------------------------------ public API

  setViewport(cx: number, cy: number, radiusPx: number): void {
    this.vpCx = cx;
    this.vpCy = cy;
    this.vpR = Math.max(4, radiusPx);
    this.viewportSet = true;
    this.updateMatrices();
  }

  setSun(lat: number, lon: number): void {
    this.sun = latLonToVec3(lat, lon);
  }

  setHome(p: LatLon | null): void {
    this.homeLL = p ? { lat: p.lat, lon: p.lon } : null;
    this.home = p ? latLonToVec3(p.lat, p.lon, 1.002) : null;
    this.rebuildActiveArcs();
  }

  setPlayers(players: GlobePlayer[]): void {
    const prev = new Map(this.players.map((r) => [r.p.id, r]));
    this.players = players.map((p) => {
      const old = prev.get(p.id);
      let arc = old && old.p.colo === p.colo && old.p.lat === p.lat && old.p.lon === p.lon ? old.arc : null;
      if (!arc && p.colo) {
        const c = coloByIata(p.colo);
        if (c && Math.abs(c.lat - p.lat) + Math.abs(c.lon - p.lon) > 0.2) arc = makeArc(p, c, 1.002);
      }
      return {
        p: { ...p },
        pos: latLonToVec3(p.lat, p.lon, 1.003),
        rgb: hsl(p.hue, 0.95, p.ghost ? 0.7 : 0.62),
        phase: hash01(p.id),
        arc,
      };
    });
  }

  setActiveColos(iatas: string[]): void {
    this.active = new Set(iatas.map((s) => s.toUpperCase()));
    this.rebuildActiveArcs();
  }

  setCursor(id: string, lat: number, lon: number, hue: number): void {
    const target = latLonToVec3(lat, lon, 1.003);
    const c = this.cursors.get(id);
    if (c) {
      c.target = target;
      c.rgb = hsl(hue, 0.9, 0.68);
    } else {
      this.cursors.set(id, { cur: target, target, rgb: hsl(hue, 0.9, 0.68) });
    }
  }

  removeCursor(id: string): void {
    this.cursors.delete(id);
  }

  pulse(lat: number, lon: number, hue: number, strength = 0.6): void {
    if (this.ripples.length >= MAX_RIPPLES) this.ripples.shift();
    this.ripples.push({
      p: latLonToVec3(lat, lon, 1.004),
      rgb: hsl(hue, 0.95, 0.62),
      strength: clamp(strength, 0, 1),
      born: performance.now(),
    });
  }

  sendPacket(path: LatLon[], hue: number, durationsMs: number[], opts: PacketOptions = {}): void {
    if (path.length < 2) {
      safeCall(opts.onArrive);
      return;
    }
    const legs: Leg[] = [];
    let t = 0;
    for (let i = 0; i < path.length - 1; i++) {
      const a = path[i]!;
      const b = path[i + 1]!;
      const dur = Math.max(1, durationsMs[i] ?? durationsMs[durationsMs.length - 1] ?? 300);
      legs.push({ ...makeArc(a, b), t0: t, dur });
      t += dur;
    }
    if (this.packets.length >= MAX_PACKETS) {
      const old = this.packets.shift();
      if (old && !old.arrived) safeCall(old.onArrive);
    }
    this.packets.push({
      legs,
      rgb: hsl(hue, 0.95, 0.62),
      width: clamp(opts.width ?? 1, 0.3, 4),
      start: performance.now(),
      total: t,
      nextWp: 1,
      arrived: false,
      onArrive: opts.onArrive,
      onWaypoint: opts.onWaypoint,
    });
  }

  project(lat: number, lon: number): { x: number; y: number; visible: boolean; facing: number } {
    const p = latLonToVec3(lat, lon);
    const c = xform4(this.mvp, p[0], p[1], p[2], 1);
    const w = c[3] || 1e-6;
    const x = (c[0] / w * 0.5 + 0.5) * this.cssW;
    const y = (1 - (c[1] / w * 0.5 + 0.5)) * this.cssH;
    const dx = this.cam[0] - p[0], dy = this.cam[1] - p[1], dz = this.cam[2] - p[2];
    const l = Math.hypot(dx, dy, dz) || 1;
    const facing = (p[0] * dx + p[1] * dy + p[2] * dz) / l;
    return { x, y, visible: facing > 0, facing };
  }

  pick(x: number, y: number): LatLon | null {
    const nx = (x / this.cssW) * 2 - 1;
    const ny = 1 - (y / this.cssH) * 2;
    const f = xform4(this.invMvp, nx, ny, 1, 1);
    const far: Vec3 = [f[0] / f[3], f[1] / f[3], f[2] / f[3]];
    const ro = this.cam;
    let rd: Vec3 = [far[0] - ro[0], far[1] - ro[1], far[2] - ro[2]];
    const l = Math.hypot(rd[0], rd[1], rd[2]) || 1;
    rd = [rd[0] / l, rd[1] / l, rd[2] / l];
    const b = ro[0] * rd[0] + ro[1] * rd[1] + ro[2] * rd[2];
    const c = ro[0] * ro[0] + ro[1] * ro[1] + ro[2] * ro[2] - 1;
    const disc = b * b - c;
    if (disc < 0) return null;
    const t = -b - Math.sqrt(disc);
    if (t < 0) return null;
    return vec3ToLatLon([ro[0] + rd[0] * t, ro[1] + rd[1] * t, ro[2] + rd[2] * t]);
  }

  focus(lat: number, lon: number, ms = 1200): void {
    const now = performance.now();
    this.velLat = this.velLon = 0;
    this.lastInteraction = now;
    this.spin = 0;
    this.focusAnim = {
      fromLat: this.camLat,
      fromLon: this.camLon,
      toLat: clamp(lat, -MAX_TILT, MAX_TILT),
      toLon: lon,
      start: now,
      dur: Math.max(1, ms),
    };
  }

  isDragging(): boolean {
    return !!this.ptr?.dragging;
  }

  /** True if the most recently finished pointer gesture was a drag (not a tap). */
  wasDrag(): boolean {
    return this.lastGestureWasDrag;
  }

  /** Current camera centre (the surface point facing the viewer). */
  getCenter(): LatLon {
    return { lat: this.camLat, lon: ((this.camLon % 360) + 540) % 360 - 180 };
  }

  /** Enables/disables idle auto-rotation. */
  autoRotate = true;

  handlePointerDown(e: PointerEvent): void {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // A second finger while a drag is in progress is ignored; a stale, non-dragging
    // pointer (e.g. an up event the host never forwarded) is replaced.
    if (this.ptr && this.ptr.dragging && e.pointerType !== "mouse") return;
    const { x, y } = this.localXY(e);
    this.ptr = { id: e.pointerId, x0: x, y0: y, x, y, t: performance.now(), dragging: false };
    this.velLat = this.velLon = 0;
    this.lastInteraction = performance.now();
    if (this.opts.attachPointerHandlers !== false && e.currentTarget === this.canvas) {
      try {
        this.canvas.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
  }

  handlePointerMove(e: PointerEvent): void {
    const p = this.ptr;
    if (!p || e.pointerId !== p.id) return;
    const { x, y } = this.localXY(e);
    const now = performance.now();
    if (!p.dragging) {
      if (Math.hypot(x - p.x0, y - p.y0) < TAP_SLOP_PX) return;
      p.dragging = true;
      this.focusAnim = null;
      safeCall(this.opts.onDragStart);
    }
    const dx = x - p.x;
    const dy = y - p.y;
    const k = 1 / DEG / this.vpR;
    const dLon = -dx * k;
    const dLat = dy * k;
    this.camLon += dLon;
    this.camLat = clamp(this.camLat + dLat, -MAX_TILT, MAX_TILT);
    const dt = Math.max(1, now - p.t);
    const a = 0.35;
    this.velLon = this.velLon * (1 - a) + (dLon / dt) * a;
    this.velLat = this.velLat * (1 - a) + (dLat / dt) * a;
    p.x = x;
    p.y = y;
    p.t = now;
    this.lastInteraction = now;
    this.spin = 0;
  }

  handlePointerUp(e: PointerEvent): void {
    const p = this.ptr;
    if (!p || e.pointerId !== p.id) return;
    const now = performance.now();
    this.lastGestureWasDrag = p.dragging;
    if (p.dragging) {
      if (now - p.t > 90) this.velLat = this.velLon = 0;
      const maxV = 0.6;
      this.velLon = clamp(this.velLon, -maxV, maxV);
      this.velLat = clamp(this.velLat, -maxV, maxV);
      safeCall(this.opts.onDragEnd);
    } else {
      this.velLat = this.velLon = 0;
    }
    this.ptr = null;
    this.lastInteraction = now;
    try {
      if (this.canvas.hasPointerCapture(e.pointerId)) this.canvas.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }

  resize(): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = Math.max(1, this.canvas.clientWidth || window.innerWidth);
    const h = Math.max(1, this.canvas.clientHeight || window.innerHeight);
    this.cssW = w;
    this.cssH = h;
    this.dpr = dpr;
    const bw = Math.round(w * dpr);
    const bh = Math.round(h * dpr);
    if (this.canvas.width !== bw) this.canvas.width = bw;
    if (this.canvas.height !== bh) this.canvas.height = bh;
    if (!this.viewportSet) {
      this.vpCx = w / 2;
      this.vpCy = h / 2;
      this.vpR = Math.min(w, h) * 0.38;
    }
    this.updateMatrices();
  }

  frame(nowMs: number): void {
    if (this.disposed) return;
    const dt = this.lastNow < 0 ? 16 : clamp(nowMs - this.lastNow, 0, 100);
    this.lastNow = nowMs;
    this.time = nowMs / 1000;
    if (this.canvas.clientWidth && (this.canvas.clientWidth !== this.cssW || this.canvas.clientHeight !== this.cssH)) {
      this.resize();
    }
    this.updateCamera(nowMs, dt);
    this.updateMatrices();
    this.updateCursors(dt);
    this.buildScene(nowMs);
    this.render();
  }

  dispose(): void {
    this.disposed = true;
    for (const [t, type, fn] of this.listeners) t.removeEventListener(type, fn);
    this.listeners = [];
    const gl = this.gl;
    const r = this.res;
    if (gl && r && !gl.isContextLost()) {
      for (const p of [r.globe, r.dots, r.ribbon, r.sprite]) gl.deleteProgram(p.prog);
      for (const v of [r.globeVao, r.dotsVao, r.ribbonVao, r.spriteVao]) gl.deleteVertexArray(v);
      for (const b of [r.dotsBuf, r.quad, r.ribbonVerts.buf, r.ribbonIdx.buf, r.spriteInst.buf]) gl.deleteBuffer(b);
    }
    this.res = null;
    this.packets = [];
  }

  // ------------------------------------------------------------ internals: setup

  private on(t: EventTarget, type: string, fn: (e: Event) => void): void {
    t.addEventListener(type, fn);
    this.listeners.push([t, type, fn]);
  }

  private localXY(e: PointerEvent): { x: number; y: number } {
    const r = this.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private initGL(): void {
    const gl = this.gl;
    if (!gl || gl.isContextLost()) return;
    try {
      const globe = createProgram(gl, GLOBE_VS, GLOBE_FS, ["aCorner"], ["uQuad", "uInvMVP", "uCam", "uSun", "uEnergy", "uTime"], "globe");
      const dots = createProgram(
        gl,
        DOTS_VS,
        DOTS_FS,
        ["aPos", "aAttr"],
        ["uMVP", "uCam", "uSun", "uPointSize", "uTime", "uEnergy"],
        "dots",
      );
      const ribbon = createProgram(
        gl,
        RIBBON_VS,
        RIBBON_FS,
        ["aPos", "aTan", "aSideWidth", "aColor", "aDash"],
        ["uMVP", "uCam", "uHalfRes", "uDpr", "uTime"],
        "ribbon",
      );
      const sprite = createProgram(
        gl,
        SPRITE_VS,
        SPRITE_FS,
        ["aCorner", "iPosSize", "iColor", "iParam"],
        ["uMVP", "uCam", "uHalfRes", "uDpr"],
        "sprite",
      );

      const quad = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

      const globeVao = gl.createVertexArray();
      gl.bindVertexArray(globeVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      setupAttribs(gl, [[0, 2]]);

      const ribbonVerts = new DynamicBuffer(gl, gl.ARRAY_BUFFER);
      const ribbonIdx = new DynamicBuffer(gl, gl.ELEMENT_ARRAY_BUFFER);
      const ribbonVao = gl.createVertexArray();
      gl.bindVertexArray(ribbonVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, ribbonVerts.buf);
      setupAttribs(gl, [[0, 3], [1, 3], [2, 2], [3, 4], [4, 2]]);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ribbonIdx.buf);

      const spriteInst = new DynamicBuffer(gl, gl.ARRAY_BUFFER);
      const spriteVao = gl.createVertexArray();
      gl.bindVertexArray(spriteVao);
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      setupAttribs(gl, [[0, 2]]);
      gl.bindBuffer(gl.ARRAY_BUFFER, spriteInst.buf);
      setupAttribs(gl, [[1, 4], [2, 4], [3, 4]], 1);
      gl.bindVertexArray(null);

      this.res = {
        globe,
        dots,
        ribbon,
        sprite,
        globeVao,
        dotsVao: null,
        dotsBuf: null,
        dotsCount: 0,
        ribbonVao,
        ribbonVerts,
        ribbonIdx,
        spriteVao,
        spriteInst,
        quad,
      };
      this.uploadLand();
    } catch (err) {
      console.error(err);
      this.res = null;
    }
  }

  private uploadLand(): void {
    const gl = this.gl;
    const r = this.res;
    const land = this.land;
    if (!gl || !r || !land || gl.isContextLost()) return;
    const interleaved = new Float32Array(land.count * 5);
    for (let i = 0; i < land.count; i++) {
      interleaved[i * 5] = land.positions[i * 3]!;
      interleaved[i * 5 + 1] = land.positions[i * 3 + 1]!;
      interleaved[i * 5 + 2] = land.positions[i * 3 + 2]!;
      interleaved[i * 5 + 3] = land.attrs[i * 2]!;
      interleaved[i * 5 + 4] = land.attrs[i * 2 + 1]!;
    }
    if (r.dotsBuf) gl.deleteBuffer(r.dotsBuf);
    if (r.dotsVao) gl.deleteVertexArray(r.dotsVao);
    r.dotsBuf = gl.createBuffer();
    r.dotsVao = gl.createVertexArray();
    gl.bindVertexArray(r.dotsVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, r.dotsBuf);
    gl.bufferData(gl.ARRAY_BUFFER, interleaved, gl.STATIC_DRAW);
    setupAttribs(gl, [[0, 3], [1, 2]]);
    gl.bindVertexArray(null);
    r.dotsCount = land.count;
  }

  private rebuildActiveArcs(): void {
    this.activeArcs = [];
    const home = this.homeLL;
    if (!home) return;
    for (const iata of this.active) {
      const c = coloByIata(iata);
      if (!c) continue;
      if (Math.abs(c.lat - home.lat) + Math.abs(c.lon - home.lon) < 0.5) continue;
      this.activeArcs.push(makeArc(c, home, 1.003));
    }
  }

  // ------------------------------------------------------------ internals: camera

  private updateCamera(now: number, dt: number): void {
    const f = this.focusAnim;
    if (f) {
      const t = clamp((now - f.start) / f.dur, 0, 1);
      const e = easeInOutCubic(t);
      this.camLat = f.fromLat + (f.toLat - f.fromLat) * e;
      this.camLon = lerpAngleDeg(f.fromLon, f.toLon, e);
      this.lastInteraction = now;
      if (t >= 1) this.focusAnim = null;
      return;
    }
    if (this.ptr?.dragging) return;
    // inertia
    if (this.velLat !== 0 || this.velLon !== 0) {
      this.camLon += this.velLon * dt;
      this.camLat = clamp(this.camLat + this.velLat * dt, -MAX_TILT, MAX_TILT);
      const decay = Math.exp(-dt / 420);
      this.velLon *= decay;
      this.velLat *= decay;
      if (Math.abs(this.velLon) + Math.abs(this.velLat) < 1e-5) this.velLat = this.velLon = 0;
    }
    // idle auto-rotation (eastward spin: the sub-viewer longitude drifts west)
    if (this.autoRotate && !this.ptr && now - this.lastInteraction > IDLE_RESUME_MS) {
      this.spin = Math.min(1, this.spin + dt / 1500);
    } else if (!this.autoRotate) {
      this.spin = 0;
    }
    this.camLon -= AUTO_SPIN_DEG_PER_MS * dt * this.spin * this.spin;
    if (this.camLon > 1e5 || this.camLon < -1e5) this.camLon %= 360;
  }

  private updateMatrices(): void {
    const W = this.cssW;
    const H = this.cssH;
    const rNdc = this.vpR / (H / 2);
    const fy = rNdc * Math.sqrt(CAM_DIST * CAM_DIST - 1);
    const ox = this.vpCx / (W / 2) - 1;
    const oy = 1 - this.vpCy / (H / 2);
    const P = shiftedPerspective(fy, W / H, 0.1, 20, ox, oy);
    const V = translation(0, 0, -CAM_DIST);
    const M = globeRotation(this.camLat, this.camLon);
    mul4(this.mvp, P, mul4(mat4(), V, M));
    invert4(this.invMvp, this.mvp);
    this.cam = [M[2]! * CAM_DIST, M[6]! * CAM_DIST, M[10]! * CAM_DIST];
  }

  private updateCursors(dt: number): void {
    const k = 1 - Math.exp(-dt / 110);
    for (const c of this.cursors.values()) {
      const x = c.cur[0] + (c.target[0] - c.cur[0]) * k;
      const y = c.cur[1] + (c.target[1] - c.cur[1]) * k;
      const z = c.cur[2] + (c.target[2] - c.cur[2]) * k;
      const l = Math.hypot(x, y, z) || 1;
      c.cur = [(x / l) * 1.003, (y / l) * 1.003, (z / l) * 1.003];
    }
  }

  // ------------------------------------------------------------ internals: scene building

  private sprite(p: Vec3, size: number, rgb: Vec3, intensity: number, shape: number, mode: number, p1 = 0, rot = 0): void {
    if (intensity <= 0.002) return;
    const s = this.sv;
    s.reserve(SPRITE_FLOATS);
    const d = s.data;
    let o = s.length;
    d[o++] = p[0]; d[o++] = p[1]; d[o++] = p[2]; d[o++] = size;
    d[o++] = rgb[0]; d[o++] = rgb[1]; d[o++] = rgb[2]; d[o++] = intensity;
    d[o++] = shape; d[o++] = mode; d[o++] = p1; d[o++] = rot;
    s.length = o;
  }

  private ribbonBegin(): void {
    this.ribbonOpen = false;
  }

  /** Appends one polyline point (two vertices) to the current ribbon. */
  private ribbonPoint(
    x: number, y: number, z: number,
    tx: number, ty: number, tz: number,
    width: number, rgb: Vec3, alpha: number, u: number, dash: number,
  ): void {
    const v = this.rv;
    v.reserve(RIBBON_FLOATS * 2);
    const d = v.data;
    let o = v.length;
    for (let side = -1; side <= 1; side += 2) {
      d[o++] = x; d[o++] = y; d[o++] = z;
      d[o++] = tx; d[o++] = ty; d[o++] = tz;
      d[o++] = side; d[o++] = width;
      d[o++] = rgb[0]; d[o++] = rgb[1]; d[o++] = rgb[2]; d[o++] = alpha;
      d[o++] = u; d[o++] = dash;
    }
    v.length = o;
    const base = this.ribbonCount;
    this.ribbonCount += 2;
    if (this.ribbonOpen) {
      const ii = this.ri;
      ii.reserve(6);
      const b0 = base - 2;
      const id = ii.data;
      let k = ii.length;
      id[k++] = b0; id[k++] = b0 + 1; id[k++] = b0 + 2;
      id[k++] = b0 + 1; id[k++] = b0 + 3; id[k++] = b0 + 2;
      ii.length = k;
    }
    this.ribbonOpen = true;
  }

  private staticArc(arc: Arc, rgb: Vec3, width: number, alpha: number, dash: number): void {
    this.ribbonBegin();
    const { pts, tans, n } = arc;
    for (let j = 0; j <= n; j++) {
      const t = j / n;
      const taper = Math.min(1, Math.min(t, 1 - t) * 8 + 0.25);
      this.ribbonPoint(
        pts[j * 3]!, pts[j * 3 + 1]!, pts[j * 3 + 2]!,
        tans[j * 3]!, tans[j * 3 + 1]!, tans[j * 3 + 2]!,
        width, rgb, alpha * taper, t * n * 0.05, dash,
      );
    }
  }

  private buildScene(now: number): void {
    this.rv.reset();
    this.ri.reset();
    this.sv.reset();
    this.ribbonCount = 0;
    const T = this.time;
    const sc = clamp(this.vpR / 320, 0.6, 1.6);
    const energy = clamp(this.energy, 0, 1);

    // --- colos
    for (const c of this.coloPos) {
      if (this.active.has(c.iata)) {
        this.sprite(c.pos, 5.5 * sc, CF_ORANGE, 1.0 + energy * 0.4, S_GLOW, BILLBOARD);
        const ph = (T / 2.6 + c.phase) % 1;
        this.sprite(c.pos, 0.008 + 0.034 * ph, CF_ORANGE, Math.pow(1 - ph, 1.5) * 0.75, S_RING, TANGENT, 0.07);
      } else {
        this.sprite(c.pos, 3.0 * sc, CF_ORANGE, 0.42, S_GLOW, BILLBOARD);
      }
    }

    // --- active colo → home links (faint, flowing towards home)
    for (const arc of this.activeArcs) this.staticArc(arc, CF_ORANGE, 1.6, 0.16, 3.0);

    // --- home beacon
    if (this.home) {
      const h = this.home;
      const n = 18;
      const H = 0.24;
      const flick = 0.88 + 0.12 * Math.sin(T * 5.3) * Math.sin(T * 1.7);
      this.ribbonBegin();
      for (let j = 0; j <= n; j++) {
        const t = j / n;
        const r = 1 + H * t;
        this.ribbonPoint(
          h[0] * r, h[1] * r, h[2] * r,
          h[0], h[1], h[2],
          (7.5 - 4.5 * t) * sc, HOME_COL, Math.pow(1 - t, 1.4) * 1.1 * flick, t, 0,
        );
      }
      const tip: Vec3 = [h[0] * (1 + H), h[1] * (1 + H), h[2] * (1 + H)];
      this.sprite(tip, 4 * sc, HOME_COL, 0.5 * flick, S_GLOW, BILLBOARD);
      this.sprite(h, 16 * sc, [1.0, 0.62, 0.3], 0.9, S_GLOW, BILLBOARD);
      this.sprite(h, 34 * sc, [1.0, 0.55, 0.2], 0.35 + energy * 0.3, S_SOFT, BILLBOARD);
      this.sprite(h, 0.058, [1.0, 0.6, 0.22], 0.95, S_BEACON, TANGENT, 3, T * 0.9);
      this.sprite(h, 0.036, [1.0, 0.9, 0.78], 0.7, S_BEACON, TANGENT, 6, -T * 1.5);
      const ph = (T / 2.2) % 1;
      this.sprite(h, 0.02 + 0.09 * ph, [1.0, 0.7, 0.4], Math.pow(1 - ph, 2) * 0.8, S_RING, TANGENT, 0.03);
    }

    // --- players
    for (const pr of this.players) {
      const { p, pos, rgb } = pr;
      const k = p.ghost ? 0.45 : 1;
      if (pr.arc) this.staticArc(pr.arc, rgb, 2.0, 0.32 * k, 4.0);
      this.sprite(pos, 6.5 * sc, rgb, 1.1 * k, S_GLOW, BILLBOARD);
      const ph = (T / 1.9 + pr.phase) % 1;
      this.sprite(pos, 0.01 + 0.03 * ph, rgb, Math.pow(1 - ph, 1.6) * 0.85 * k, S_RING, TANGENT, 0.06);
      if (p.isYou) {
        this.sprite(pos, 0.032, mix3(rgb, [1, 1, 1], 0.45), 1.0, S_BEACON, TANGENT, 4, T * 1.2);
        this.sprite(pos, 26 * sc, rgb, 0.4, S_SOFT, BILLBOARD);
      }
    }

    // --- cursors
    for (const c of this.cursors.values()) {
      this.sprite(c.cur, 0.03, c.rgb, 0.95, S_CROSS, TANGENT);
    }

    // --- ripples
    let wr = 0;
    for (const r of this.ripples) {
      const t = (now - r.born) / RIPPLE_MS;
      if (t >= 1) continue;
      this.ripples[wr++] = r;
      if (t < 0) continue;
      const e = 1 - Math.pow(1 - t, 3);
      const s = r.strength;
      const size = 0.012 + (0.045 + 0.11 * s) * e;
      this.sprite(r.p, size, r.rgb, Math.pow(1 - t, 1.5) * (0.55 + 0.75 * s), S_RING, TANGENT, 0.035);
      const t2 = t - 0.18;
      if (t2 > 0) {
        const e2 = 1 - Math.pow(1 - t2, 3);
        this.sprite(r.p, 0.01 + (0.03 + 0.07 * s) * e2, r.rgb, Math.pow(1 - t, 2) * 0.5 * (0.5 + s), S_RING, TANGENT, 0.05);
      }
      this.sprite(r.p, (8 + 10 * s) * sc, r.rgb, Math.pow(1 - t, 3) * (0.6 + 0.6 * s), S_GLOW, BILLBOARD);
    }
    this.ripples.length = wr;

    // --- packets
    let wp = 0;
    for (const pk of this.packets) {
      if (this.buildPacket(pk, now, sc)) this.packets[wp++] = pk;
    }
    this.packets.length = wp;

    // --- flashes
    let wf = 0;
    for (const f of this.flashes) {
      const t = (now - f.born) / FLASH_MS;
      if (t >= 1) continue;
      this.flashes[wf++] = f;
      const e = 1 - Math.pow(1 - t, 2);
      this.sprite(f.p, (6 + 16 * e) * sc, mix3(f.rgb, [1, 1, 1], 0.3), Math.pow(1 - t, 1.5) * 0.9, S_RING, BILLBOARD, 0.05);
      this.sprite(f.p, 10 * sc, f.rgb, Math.pow(1 - t, 2.5) * 1.2, S_GLOW, BILLBOARD);
    }
    this.flashes.length = wf;
  }

  /** Appends a packet's trail and head. Returns false once it can be dropped. */
  private buildPacket(pk: Packet, now: number, sc: number): boolean {
    const el = now - pk.start;
    // waypoint + arrival events
    while (pk.nextWp <= pk.legs.length) {
      const leg = pk.legs[pk.nextWp - 1]!;
      if (el < leg.t0 + leg.dur) break;
      const o = leg.n * 3;
      const p: Vec3 = [leg.pts[o]!, leg.pts[o + 1]!, leg.pts[o + 2]!];
      this.flashes.push({ p, rgb: pk.rgb, born: now });
      const i = pk.nextWp;
      pk.nextWp++;
      if (pk.onWaypoint) safeCall(() => pk.onWaypoint!(i));
    }
    if (!pk.arrived && el >= pk.total) {
      pk.arrived = true;
      safeCall(pk.onArrive);
    }
    if (el > pk.total + TRAIL_LINGER_MS + 200) return false;

    const w = pk.width;
    const white: Vec3 = [1, 1, 1];
    const hot = mix3(pk.rgb, white, 0.35);
    for (const leg of pk.legs) {
      if (el < leg.t0) break;
      if (el - (leg.t0 + leg.dur) > TRAIL_LINGER_MS + 100) continue;
      const headT = clamp((el - leg.t0) / leg.dur, 0, 1);
      const tau = clamp(leg.dur * 0.3, 70, 450);
      const { pts, tans, n } = leg;
      const headF = headT * n;
      const last = Math.floor(headF);
      this.ribbonBegin();
      const put = (x: number, y: number, z: number, tx: number, ty: number, tz: number, tv: number) => {
        const age = Math.max(0, el - tv);
        const trail = Math.exp(-age / tau);
        const linger = 0.2 * (1 - clamp(age / TRAIL_LINGER_MS, 0, 1));
        const a = Math.max(trail, linger);
        const width = (2.2 + 4.8 * trail) * w * sc;
        this.ribbonPoint(x, y, z, tx, ty, tz, width, trail > 0.4 ? hot : pk.rgb, a * 1.1, 0, 0);
      };
      for (let j = 0; j <= Math.min(last, n); j++) {
        const o = j * 3;
        put(pts[o]!, pts[o + 1]!, pts[o + 2]!, tans[o]!, tans[o + 1]!, tans[o + 2]!, leg.t0 + (leg.dur * j) / n);
      }
      if (headT < 1) {
        const f = headF - last;
        const o = last * 3;
        const o2 = Math.min(n, last + 1) * 3;
        const x = pts[o]! + (pts[o2]! - pts[o]!) * f;
        const y = pts[o + 1]! + (pts[o2 + 1]! - pts[o + 1]!) * f;
        const z = pts[o + 2]! + (pts[o2 + 2]! - pts[o + 2]!) * f;
        put(x, y, z, tans[o]!, tans[o + 1]!, tans[o + 2]!, el);
        const head: Vec3 = [x, y, z];
        this.sprite(head, 7 * w * sc, hot, 1.25, S_GLOW, BILLBOARD);
        this.sprite(head, 20 * w * sc, pk.rgb, 0.45, S_SOFT, BILLBOARD);
      }
    }
    return true;
  }

  // ------------------------------------------------------------ internals: render

  private render(): void {
    const gl = this.gl;
    const r = this.res;
    if (!gl || !r || gl.isContextLost()) return;
    const bw = this.canvas.width;
    const bh = this.canvas.height;
    gl.viewport(0, 0, bw, bh);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.CULL_FACE);
    const energy = clamp(this.energy, 0, 1);
    const halfRes: [number, number] = [bw / 2, bh / 2];

    // 1. ocean + atmosphere
    gl.disable(gl.BLEND);
    gl.useProgram(r.globe.prog);
    const hx = (1.75 * this.vpR) / (this.cssW / 2);
    const hy = (1.75 * this.vpR) / (this.cssH / 2);
    const ox = this.vpCx / (this.cssW / 2) - 1;
    const oy = 1 - this.vpCy / (this.cssH / 2);
    gl.uniform4f(r.globe.u["uQuad"]!, ox, oy, hx, hy);
    gl.uniformMatrix4fv(r.globe.u["uInvMVP"]!, false, this.invMvp);
    gl.uniform3fv(r.globe.u["uCam"]!, this.cam);
    gl.uniform3fv(r.globe.u["uSun"]!, this.sun);
    gl.uniform1f(r.globe.u["uEnergy"]!, energy);
    gl.uniform1f(r.globe.u["uTime"]!, this.time);
    gl.bindVertexArray(r.globeVao);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);

    // 2. land dots
    if (r.dotsVao && r.dotsCount > 0 && this.land) {
      gl.useProgram(r.dots.prog);
      const spacing = this.vpR * this.dpr * Math.sqrt((4 * Math.PI) / this.land.samples);
      gl.uniformMatrix4fv(r.dots.u["uMVP"]!, false, this.mvp);
      gl.uniform3fv(r.dots.u["uCam"]!, this.cam);
      gl.uniform3fv(r.dots.u["uSun"]!, this.sun);
      gl.uniform1f(r.dots.u["uPointSize"]!, Math.max(1.5, spacing * 0.78));
      gl.uniform1f(r.dots.u["uTime"]!, this.time);
      gl.uniform1f(r.dots.u["uEnergy"]!, energy);
      gl.bindVertexArray(r.dotsVao);
      gl.drawArrays(gl.POINTS, 0, r.dotsCount);
    }

    // 3. ribbons
    if (this.ri.length > 0) {
      gl.useProgram(r.ribbon.prog);
      gl.uniformMatrix4fv(r.ribbon.u["uMVP"]!, false, this.mvp);
      gl.uniform3fv(r.ribbon.u["uCam"]!, this.cam);
      gl.uniform2f(r.ribbon.u["uHalfRes"]!, halfRes[0], halfRes[1]);
      gl.uniform1f(r.ribbon.u["uDpr"]!, this.dpr);
      gl.uniform1f(r.ribbon.u["uTime"]!, this.time);
      gl.bindVertexArray(r.ribbonVao);
      r.ribbonVerts.upload(this.rv.data, this.rv.length);
      r.ribbonIdx.upload(this.ri.data, this.ri.length);
      gl.drawElements(gl.TRIANGLES, this.ri.length, gl.UNSIGNED_INT, 0);
    }

    // 4. sprites
    const nSprites = this.sv.length / SPRITE_FLOATS;
    if (nSprites > 0) {
      gl.useProgram(r.sprite.prog);
      gl.uniformMatrix4fv(r.sprite.u["uMVP"]!, false, this.mvp);
      gl.uniform3fv(r.sprite.u["uCam"]!, this.cam);
      gl.uniform2f(r.sprite.u["uHalfRes"]!, halfRes[0], halfRes[1]);
      gl.uniform1f(r.sprite.u["uDpr"]!, this.dpr);
      gl.bindVertexArray(r.spriteVao);
      r.spriteInst.upload(this.sv.data, this.sv.length);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, nSprites);
    }
    gl.bindVertexArray(null);
  }
}
